import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Text } from '@gravity-ui/uikit';
import { monthShort, monthTitle } from './format.js';

/**
 * Помесячный график: пары столбцов и линия итога. Свой SVG, а не библиотека
 * графиков: два ряда и линия — это сорок строк разметки, а не сотни
 * килобайт сборки. Рядом — та же таблица для скринридера.
 */

export interface ChartSeries {
  key: string;
  label: string;
  /** CSS-цвет; лучше переменная Gravity, чтобы тема меняла и график. */
  color: string;
  values: readonly number[];
}

const WIDTH = 760;
const PADDING = { top: 12, right: 8, bottom: 26, left: 64 };

function niceStep(range: number): number {
  const rough = range / 4;
  const power = 10 ** Math.floor(Math.log10(Math.max(rough, 1)));
  const scaled = rough / power;
  const nice = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 2.5 ? 2.5 : scaled <= 5 ? 5 : 10;
  return nice * power;
}

export function MonthChart({
  months,
  series,
  line,
  height = 240,
  format,
  formatAxis,
  title,
}: {
  months: readonly string[];
  series: readonly ChartSeries[];
  line?: ChartSeries | undefined;
  height?: number;
  format: (value: number) => string;
  formatAxis: (value: number) => string;
  title: string;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const tableId = useId();
  const host = useRef<HTMLDivElement>(null);
  // Ширина viewBox = ширина контейнера, иначе подписи на телефоне сжимаются до 5px.
  const [width, setWidth] = useState(WIDTH);
  useEffect(() => {
    const element = host.current;
    if (element === null) return;
    const update = () => {
      setWidth(Math.max(280, Math.round(element.clientWidth)));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  const scale = useMemo(() => {
    const values = [...series.flatMap((row) => row.values), ...(line?.values ?? [])];
    const max = Math.max(0, ...values);
    const min = Math.min(0, ...values);
    // Пустой ряд не рисуем в шкале «1 копейка»: ось тогда врёт «0,01 ₽».
    if (max === 0 && min === 0) return { top: 1, bottom: 0, ticks: [0] };
    const step = niceStep(max - min);
    const top = Math.ceil(max / step) * step;
    const bottom = Math.floor(min / step) * step;
    const ticks: number[] = [];
    for (let value = bottom; value <= top + step / 2; value += step) ticks.push(value);
    return { top, bottom, ticks };
  }, [series, line]);

  const plotHeight = height - PADDING.top - PADDING.bottom;
  const plotWidth = width - PADDING.left - PADDING.right;
  const band = plotWidth / Math.max(months.length, 1);
  const group = band * 0.62;
  const barWidth = group / Math.max(series.length, 1);
  const y = (value: number) => PADDING.top + ((scale.top - value) / (scale.top - scale.bottom || 1)) * plotHeight;
  const x = (index: number) => PADDING.left + band * index + band / 2;

  const linePath =
    line === undefined
      ? null
      : line.values.map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(' ');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        {[...series, ...(line === undefined ? [] : [line])].map((row) => (
          <span key={row.key} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={row === line ? 'h-[3px] w-4 rounded-full' : 'size-2.5 rounded-sm'}
              style={{ background: row.color }}
            />
            <Text variant="caption-2" color="secondary">
              {row.label}
            </Text>
          </span>
        ))}
      </div>

      <div className="relative" ref={host}>
        <svg
          viewBox={`0 0 ${String(width)} ${String(height)}`}
          className="block h-auto w-full"
          role="img"
          aria-label={title}
          aria-describedby={tableId}
          onPointerLeave={() => {
            setHovered(null);
          }}
        >
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={PADDING.left}
                x2={width - PADDING.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke="var(--g-color-line-generic)"
                strokeDasharray={tick === 0 ? undefined : '3 4'}
              />
              <text
                x={PADDING.left - 10}
                y={y(tick)}
                dy="0.32em"
                textAnchor="end"
                fontSize="11"
                fill="var(--g-color-text-secondary)"
                className="tabular"
              >
                {formatAxis(tick)}
              </text>
            </g>
          ))}

          {months.map((month, index) => (
            <g key={month}>
              {hovered === index ? (
                <rect
                  x={PADDING.left + band * index + 2}
                  y={PADDING.top}
                  width={band - 4}
                  height={plotHeight}
                  rx={6}
                  fill="var(--g-color-base-simple-hover)"
                />
              ) : null}
              {series.map((row, seriesIndex) => {
                const value = row.values[index] ?? 0;
                const top = y(Math.max(value, 0));
                const bottom = y(Math.min(value, 0));
                return (
                  <rect
                    key={row.key}
                    x={x(index) - group / 2 + barWidth * seriesIndex + 1}
                    y={top}
                    width={Math.max(barWidth - 2, 2)}
                    height={Math.max(bottom - top, value === 0 ? 0 : 1.5)}
                    rx={2}
                    fill={row.color}
                  />
                );
              })}
              <text
                x={x(index)}
                y={height - 8}
                textAnchor="middle"
                fontSize="11"
                fill={hovered === index ? 'var(--g-color-text-primary)' : 'var(--g-color-text-secondary)'}
              >
                {monthShort(month)}
              </text>
              <rect
                x={PADDING.left + band * index}
                y={PADDING.top}
                width={band}
                height={plotHeight + PADDING.bottom}
                fill="transparent"
                onPointerEnter={() => {
                  setHovered(index);
                }}
              />
            </g>
          ))}

          {linePath === null || line === undefined ? null : (
            <g pointerEvents="none">
              <path d={linePath} fill="none" stroke={line.color} strokeWidth={2} strokeLinejoin="round" />
              {line.values.map((value, index) => (
                <circle
                  key={months[index]}
                  cx={x(index)}
                  cy={y(value)}
                  r={hovered === index ? 4.5 : 3}
                  fill="var(--g-color-base-background)"
                  stroke={line.color}
                  strokeWidth={2}
                />
              ))}
            </g>
          )}
        </svg>

        {hovered === null ? null : (
          <div
            className="pointer-events-none absolute top-2 z-10 min-w-48 -translate-x-1/2 rounded-lg border border-[var(--g-color-line-generic)] bg-[var(--g-color-base-float)] px-3 py-2 shadow-[0_6px_20px_rgba(0,0,0,0.12)]"
            style={{ left: `${String(Math.min(Math.max((x(hovered) / width) * 100, 14), 86))}%` }}
          >
            <Text variant="subheader-1">{monthTitle(months[hovered] ?? '')}</Text>
            <div className="mt-1 flex flex-col gap-0.5">
              {[...series, ...(line === undefined ? [] : [line])].map((row) => (
                <div key={row.key} className="flex items-center justify-between gap-4">
                  <Text variant="caption-2" color="secondary">
                    {row.label}
                  </Text>
                  <Text variant="caption-2" className="tabular" whiteSpace="nowrap">
                    {format(row.values[hovered] ?? 0)}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <table id={tableId} className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">Месяц</th>
            {[...series, ...(line === undefined ? [] : [line])].map((row) => (
              <th key={row.key} scope="col">
                {row.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {months.map((month, index) => (
            <tr key={month}>
              <th scope="row">{monthTitle(month)}</th>
              {[...series, ...(line === undefined ? [] : [line])].map((row) => (
                <td key={row.key}>{format(row.values[index] ?? 0)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
